import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Ticket, Calendar, MapPin, Loader2, Clock, ChevronRight, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ticketsApi } from '../lib/api';
import { usePublicAuth } from '../context/PublicAuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const TicketCard = ({ ticket, index, onSelect }) => {
  const isValid = ['valid', 'confirmed'].includes(ticket.status);
  const eventDate = ticket.event_date ? new Date(ticket.event_date) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      onClick={() => onSelect(ticket)}
      className="group cursor-pointer relative overflow-hidden rounded-2xl"
      data-testid={`ticket-${ticket.id}`}
    >
      {/* Card with event image background */}
      <div className="relative h-[220px] md:h-[240px]">
        {/* Background image */}
        {ticket.event_image_url ? (
          <img
            src={ticket.event_image_url}
            alt={ticket.event_title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900" />
        )}

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

        {/* Status badge */}
        <div className="absolute top-4 left-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider backdrop-blur-sm ${
            isValid
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isValid ? 'bg-emerald-400' : 'bg-zinc-400'}`} />
            {isValid ? 'Activo' : 'Usado'}
          </span>
        </div>

        {/* Tier badge */}
        {ticket.tier_name && (
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30 backdrop-blur-sm">
              {ticket.tier_name}
            </span>
          </div>
        )}

        {/* Content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight text-white mb-2 line-clamp-1">
            {ticket.event_title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {eventDate && (
              <div className="flex items-center gap-1.5 text-zinc-300">
                <Calendar size={13} className="text-[#D4AF37]" />
                <span className="font-medium">
                  {format(eventDate, "d MMM yyyy", { locale: es })}
                </span>
              </div>
            )}
            {(ticket.venue_name || ticket.event_location) && (
              <div className="flex items-center gap-1.5 text-zinc-300">
                <MapPin size={13} className="text-[#D4AF37]" />
                <span className="font-medium line-clamp-1">
                  {ticket.venue_name || ticket.event_location}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dashed separator line (ticket style) */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, #D4AF37 0, #D4AF37 6px, transparent 6px, transparent 12px)',
          opacity: 0.3
        }} />
      </div>

      {/* Bottom section - ticket stub */}
      <div className="bg-zinc-900/95 backdrop-blur-sm px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
            <QRCodeSVG
              value={`BRAVO|${ticket.ticket_code}|${ticket.event_id}`}
              size={28}
              level="L"
              bgColor="transparent"
              fgColor="#D4AF37"
            />
          </div>
          <div>
            <p className="font-mono text-xs text-zinc-400 tracking-wide">{ticket.ticket_code}</p>
            <p className="text-[11px] text-zinc-500">{ticket.buyer_name}</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-zinc-500 group-hover:text-[#D4AF37] transition-colors" />
      </div>
    </motion.div>
  );
};

const TicketModal = ({ ticket, onClose }) => {
  if (!ticket) return null;
  const isValid = ['valid', 'confirmed'].includes(ticket.status);
  const eventDate = ticket.event_date ? new Date(ticket.event_date) : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm"
        data-testid="ticket-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-black/80 backdrop-blur-sm border border-zinc-700 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500 transition-all"
          data-testid="close-ticket-modal"
        >
          <X size={18} />
        </button>

        {/* Premium Ticket design */}
        <div className="relative overflow-hidden rounded-3xl">
          {/* Golden border glow effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-[#D4AF37]/30 via-transparent to-[#D4AF37]/20 p-[1px]">
            <div className="absolute inset-0 rounded-3xl bg-[#0A0A0A]" />
          </div>

          <div className="relative bg-gradient-to-b from-[#121212] to-[#0A0A0A] rounded-3xl border border-[#D4AF37]/30">
            {/* Top: Event image with premium overlay */}
            <div className="relative h-56 overflow-hidden rounded-t-3xl">
              {ticket.event_image_url ? (
                <img
                  src={ticket.event_image_url}
                  alt={ticket.event_title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
              )}
              {/* Multi-layer gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
              
              {/* Decorative corner accent */}
              <div className="absolute top-0 right-0 w-32 h-32">
                <div className="absolute top-3 right-3 w-24 h-24 border-t-2 border-r-2 border-[#D4AF37]/30 rounded-tr-2xl" />
              </div>

              {/* Status badge - premium style */}
              <div className="absolute top-4 left-4">
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider backdrop-blur-md shadow-lg ${
                  isValid
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                    : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/40'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${isValid ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-400'}`} />
                  {isValid ? 'Entrada Activa' : 'Entrada Usada'}
                </span>
              </div>

              {/* Event title on image */}
              <div className="absolute bottom-4 left-5 right-5">
                <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Evento</p>
                <h2 className="text-2xl font-black uppercase tracking-tight text-white leading-tight drop-shadow-lg">
                  {ticket.event_title}
                </h2>
              </div>
            </div>

            {/* Middle: Event info - premium card style */}
            <div className="px-5 py-4 space-y-3 border-y border-dashed border-[#D4AF37]/20 bg-gradient-to-b from-[#0A0A0A] to-[#121212]">
              {eventDate && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                    <Calendar size={16} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Fecha</p>
                    <p className="text-sm text-white font-medium">{format(eventDate, "EEEE d 'de' MMMM", { locale: es })}</p>
                  </div>
                </div>
              )}
              {(ticket.venue_name || ticket.event_location) && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                    <MapPin size={16} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Ubicación</p>
                    <p className="text-sm text-white font-medium">{ticket.venue_name || ticket.event_location}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                  <Ticket size={16} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Tipo de Entrada</p>
                  <p className="text-sm text-white font-medium">{ticket.tier_name || 'General'}</p>
                </div>
              </div>
            </div>

            {/* Bottom: QR Code - premium style */}
            <div className="px-5 py-6 flex flex-col items-center bg-gradient-to-b from-[#121212] to-[#0A0A0A] rounded-b-3xl">
              {/* QR Container with golden accent */}
              <div className="relative p-1 rounded-2xl bg-gradient-to-br from-[#D4AF37]/40 via-[#D4AF37]/20 to-[#D4AF37]/40 shadow-[0_0_30px_rgba(212,175,55,0.15)]">
                <div className="bg-white p-4 rounded-xl">
                  <QRCodeSVG
                    value={`BRAVO|${ticket.ticket_code}|${ticket.event_id}`}
                    size={160}
                    level="H"
                    bgColor="#ffffff"
                    fgColor="#0A0A0A"
                  />
                </div>
              </div>
              
              {/* Ticket code */}
              <div className="mt-5 text-center">
                <p className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1">Código de Entrada</p>
                <p className="font-mono text-xl font-black tracking-[0.15em] text-white">
                  {ticket.ticket_code}
                </p>
              </div>
              
              {/* Buyer info */}
              <div className="mt-3 px-4 py-2 bg-zinc-800/50 rounded-full">
                <p className="text-xs text-zinc-400">
                  <span className="text-zinc-500">Titular:</span> {ticket.buyer_name}
                </p>
              </div>
              
              {/* Instructions */}
              <p className="text-[11px] text-zinc-600 mt-4 text-center max-w-[200px]">
                Presentá este QR o tu DNI en la entrada del evento
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MyTicketsPage = () => {
  const { user, isAuthenticated } = usePublicAuth();
  const [email, setEmail] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
      loadTickets(user.email);
    }
  }, [isAuthenticated, user]);

  const loadTickets = async (userEmail) => {
    setLoading(true);
    try {
      const response = await ticketsApi.getMyTickets(userEmail);
      setTickets(response.data);
      setSearched(true);
      if (response.data.length === 0) {
        toast.info('No tenes tickets todavia');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }
    loadTickets(email);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Header */}
      <section className="pt-8 md:pt-32 pb-12 px-6 lg:px-12" data-testid="my-tickets-header">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-6xl font-black tracking-tighter uppercase mb-4">
              Mis Entradas
            </h1>
            {isAuthenticated ? (
              <p className="text-zinc-500 text-base max-w-xl mx-auto">
                Tus entradas compradas con <span className="text-white">{user?.email}</span>
              </p>
            ) : (
              <p className="text-zinc-500 text-base max-w-xl mx-auto">
                Ingresa tu email para ver tus entradas y codigos QR.
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Search Form */}
      {!isAuthenticated && (
        <section className="pb-10 px-6 lg:px-12">
          <div className="max-w-md mx-auto">
            <form onSubmit={handleSearch} className="flex gap-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="input-bravo flex-1"
                data-testid="tickets-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary"
                data-testid="tickets-search-btn"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              </Button>
            </form>
          </div>
        </section>
      )}

      {/* Loading */}
      {isAuthenticated && loading && (
        <section className="pb-12 px-6 lg:px-12">
          <div className="max-w-xl mx-auto text-center">
            <Loader2 size={32} className="animate-spin text-[#D4AF37] mx-auto" />
            <p className="text-zinc-500 mt-4">Cargando tus entradas...</p>
          </div>
        </section>
      )}

      {/* Tickets Grid */}
      <section className="pb-24 px-6 lg:px-12" data-testid="tickets-list">
        <div className="max-w-5xl mx-auto">
          {!searched && !isAuthenticated ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
                <Ticket size={28} className="text-zinc-600" />
              </div>
              <p className="text-zinc-500">Ingresa tu email para ver tus entradas</p>
            </div>
          ) : tickets.length === 0 && searched ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center">
                <Ticket size={28} className="text-zinc-600" />
              </div>
              <h3 className="text-xl font-bold uppercase mb-2 text-zinc-300">
                Sin entradas
              </h3>
              <p className="text-zinc-500">No hay entradas asociadas a este email.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {tickets.map((ticket, index) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  index={index}
                  onSelect={setSelectedTicket}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ticket Modal */}
      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default MyTicketsPage;
